import { useEffect, useMemo, useState } from "react";
import { useHoverDisclosure } from "@/hooks/useHoverDisclosure";
import { useReactionSummaryQuery } from "@/hooks/useReactionSummaryQuery";
import type { ReactionBreakdown, ReactionType } from "@/lib/humhub-api/types";
import { REACTION_ASSETS, REACTION_ORDER } from "@/lib/utils/reactionAssets";

interface UseReactionSummaryPopoverOptions {
  contentId?: number | string | null;
  objectModel?: string;
  objectId?: number | string | null;
  refreshKey?: number;
  reactionBreakdown?: ReactionBreakdown;
}

export const useReactionSummaryPopover = ({
  contentId,
  objectModel,
  objectId,
  refreshKey,
  reactionBreakdown,
}: UseReactionSummaryPopoverOptions) => {
  const [activeEmotion, setActiveEmotion] = useState<ReactionType | undefined>(
    undefined,
  );
  const { open, setOpen, openNow, scheduleClose } = useHoverDisclosure({
    closeDelay: 120,
  });

  const query = useReactionSummaryQuery({
    contentId,
    objectModel,
    objectId,
    enabled: open,
    emotion: activeEmotion,
  });
  const { shouldRender, refetch } = query;

  useEffect(() => {
    if (!open) return;
    if (refreshKey === undefined || refreshKey === 0 || !shouldRender) return;

    void refetch({ throwOnError: false, cancelRefetch: false });
  }, [open, refreshKey, shouldRender, refetch]);

  const chips = useMemo(
    () =>
      REACTION_ORDER.map((type) => ({
        type,
        count: reactionBreakdown?.[type] ?? 0,
      })).filter(({ count }) => count > 0),
    [reactionBreakdown],
  );

  const activeEmotionCount =
    activeEmotion == null
      ? undefined
      : (reactionBreakdown?.[activeEmotion] ?? query.total);
  const activeEmotionLabel =
    activeEmotion && REACTION_ASSETS[activeEmotion]
      ? REACTION_ASSETS[activeEmotion].label
      : undefined;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setActiveEmotion(undefined);
    }
  };

  return {
    ...query,
    open,
    chips,
    activeEmotion,
    activeEmotionCount,
    activeEmotionLabel,
    openNow,
    scheduleClose,
    setActiveEmotion,
    handleOpenChange,
  };
};
